import { ArrowRight, Check, Pencil } from "@tamagui/lucide-icons";
import type { ReactNode, Ref } from "react";
import type { TextInput } from "react-native";
import { ScrollView } from "tamagui";

import {
  Button,
  Input,
  Paragraph,
  Separator,
  Square,
  XStack,
  YStack,
} from "@/components";
import SkipOrSubmit from "./SkipOrSubmit";

export type ChoiceValue = { selected: string[]; otherText: string };

export const BORDERLESS_INPUT = {
  borderWidth: 0,
  backgroundColor: "transparent",
  paddingHorizontal: 0,
  paddingVertical: 0,
  minHeight: 0,
  marginBottom: 0,
  outlineWidth: 0,
  outlineStyle: "none",
} as const;

export function CheckboxBadge({ checked }: { checked: boolean }) {
  return (
    <Square
      size="$300"
      borderRadius="$50"
      borderWidth="$strokeWidth"
      borderColor={checked ? "$fillButtonPrimary" : "$textSecondary"}
      backgroundColor={checked ? "$fillButtonPrimary" : "$transparent"}
      alignItems="center"
      justifyContent="center"
    >
      {checked ? <Check size={16} color="$textButtonPrimary" /> : null}
    </Square>
  );
}

function FilledBadge({
  checked,
  children,
}: {
  checked: boolean;
  children: ReactNode;
}) {
  return (
    <Square
      size="$300"
      borderRadius="$50"
      backgroundColor={checked ? "$fillButtonPrimary" : "$fillNeutral"}
      alignItems="center"
      justifyContent="center"
    >
      {checked ? <Check size={16} color="$textButtonPrimary" /> : children}
    </Square>
  );
}

export function NumberBadge({
  checked,
  index,
}: {
  checked: boolean;
  index: number;
}) {
  return (
    <FilledBadge checked={checked}>
      <Paragraph size="$sm" color="$textSecondary">
        {index + 1}
      </Paragraph>
    </FilledBadge>
  );
}

export function PencilBadge() {
  return (
    <FilledBadge checked={false}>
      <Pencil size={16} color="$textSecondary" />
    </FilledBadge>
  );
}

export function OptionBodyHoverWrapper({ children }: { children: ReactNode }) {
  return (
    <XStack
      group
      flex={1}
      borderRadius="$150"
      hoverStyle={{ backgroundColor: "$navHover" }}
    >
      {children}
    </XStack>
  );
}

export function OptionBody({
  label,
  checked,
  badge,
  onPress,
  showArrow,
  dataTrackingId = "askUserQuestionOption",
}: {
  label: string;
  checked: boolean;
  badge: ReactNode;
  onPress: () => void;
  showArrow?: boolean;
  dataTrackingId?: string;
}) {
  return (
    <Button.Wrapper
      dataTrackingId={dataTrackingId}
      onPress={onPress}
      paddingHorizontal="$150"
      paddingVertical="$200"
      aria-pressed={checked}
      flex={1}
      gap="$100"
      alignItems="center"
    >
      {badge}
      <Paragraph flex={1}>{label}</Paragraph>
      {showArrow && (
        <ArrowRight
          size="$200"
          color="$textPrimary"
          opacity={0}
          $group-hover={{ opacity: 1 }}
        />
      )}
    </Button.Wrapper>
  );
}

export function OtherTextInput({
  value,
  onChangeText,
  onSubmitEditing,
  inputRef,
}: {
  value: string;
  onChangeText: (value: string) => void;
  onSubmitEditing?: () => void;
  inputRef?: Ref<TextInput>;
}) {
  return (
    <Input
      ref={inputRef}
      flex={1}
      rootProps={{ flex: 1 }}
      paddingHorizontal={0}
      placeholder="Enter answer here"
      value={value}
      onChangeText={onChangeText}
      onSubmitEditing={onSubmitEditing}
      frameProps={BORDERLESS_INPUT}
    />
  );
}

/** Shrinks to fit the panel and scrolls on overflow so siblings after it stay visible. */
export function QuestionScrollArea({ children }: { children: ReactNode }) {
  return (
    <ScrollView flexGrow={0} flexShrink={1} flexBasis="auto" minHeight={0}>
      {children}
    </ScrollView>
  );
}

export function OptionSeparator() {
  return <Separator borderColor="$strokeSubdued" marginHorizontal="$150" />;
}

export function SingleSelectInline({
  options,
  value,
  onChange,
  onSubmit,
  onSkip,
  isLast,
}: {
  options: string[];
  value: ChoiceValue;
  onChange: (value: ChoiceValue) => void;
  onSubmit: () => void;
  onSkip: () => void;
  isLast: boolean;
}) {
  const lastIndex = options.length - 1;

  return (
    <>
      {options.slice(0, -1).map((option, index) => (
        <YStack key={option}>
          {index > 0 && <OptionSeparator />}
          <OptionBodyHoverWrapper>
            <OptionBody
              label={option}
              checked={value.selected.includes(option)}
              showArrow
              badge={
                <NumberBadge
                  checked={value.selected.includes(option)}
                  index={index}
                />
              }
              onPress={() => {
                onChange({ selected: [option], otherText: "" });
                onSubmit();
              }}
            />
          </OptionBodyHoverWrapper>
        </YStack>
      ))}
      <OptionSeparator />
      <OptionBodyHoverWrapper>
        <XStack flex={1} alignItems="center" gap="$200" marginRight="$150">
          <OptionBody
            label={options[lastIndex]}
            checked={value.selected.includes(options[lastIndex])}
            badge={
              <NumberBadge
                checked={value.selected.includes(options[lastIndex])}
                index={lastIndex}
              />
            }
            onPress={() => {
              onChange({ selected: [options[lastIndex]], otherText: "" });
              onSubmit();
            }}
          />
          <SkipOrSubmit isLast={isLast} onSkip={onSkip} onSubmit={onSubmit} />
        </XStack>
      </OptionBodyHoverWrapper>
    </>
  );
}
